import { MDBBtn, MDBCardBody, MDBCardFooter, MDBContainer, MDBIcon, MDBInput, MDBRow, MDBTextArea, MDBTypography } from "mdb-react-ui-kit";
import React, { useEffect, useState } from "react";
import { Form } from 'react-final-form';
import { useDict } from '../../../contexts/LanguageContext';
import { appendLocale } from '../../../utils/helper';

const Comment = ({ comments, onSubmit, onCommentInputChange, onReplyInputChange, onReplySubmit, onDelete, isLoading }) => {
    const [input, setInput] = useState("");
    const [replyInputs, setReplyInputs] = useState([]);
    const [expand, setExpand] = useState([]);
    const [isInputEmpty, setIsInputEmpty] = useState(true);

    useEffect(() => {
        setExpand(prevExpand => {
            const newExpand = { ...prevExpand };
            for (const key in newExpand) {
                newExpand[key] = true;
            }
            return newExpand;
        }, [replyInputs]);
    }, [replyInputs]);

    const dict = useDict();

    const handleToggleReply = (key) => {
        setExpand(prevExpand => {
            const newExpand = { ...prevExpand };
            newExpand[key] = !newExpand[key];
            return newExpand;
        });
    };

    const handleSubmit = (values) => {
        const { comment: inputValue } = values;
        onSubmit(inputValue);
        setInput("");
        setIsInputEmpty(true);
    };

    const handleReplySubmit = (index, parentCommentId) => {
        onReplySubmit(parentCommentId);
        setReplyInputs(prevInputs => {
            const newInputs = [...prevInputs];
            newInputs[index] = '';
            return newInputs;
        });
        setExpand(prevExpand => {
            return { ...prevExpand, [index]: true };
        });
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        setInput(inputValue);
        onCommentInputChange(inputValue);
        setIsInputEmpty(inputValue.trim() === '');
    };

    const handleReplyInputChange = (index, e) => {
        const replyValue = e.target.value;
        setReplyInputs(prevInputs => {
            const newInputs = [...prevInputs];
            newInputs[index] = replyValue;
            return newInputs;
        });
        onReplyInputChange(replyValue);
    };

    const handleDelete = (commentId) => {
        onDelete(commentId);
    };

    const renderReplyForm = (commentItem, index, parentCommentId) => (
        <Form
            onSubmit={() => handleReplySubmit(index, parentCommentId)}
            initialValues={{ comment: '' }}
            render={({ handleSubmit }) => (
                <form onSubmit={(e) => handleSubmit(e, commentItem.isReply ? commentItem.reply_id : commentItem.id)}>
                    <div className="d-flex align-items-center mb-3">
                        <MDBInput
                            wrapperClass="form-control me-2 comment-reply-input"
                            label={dict.replyFieldText}
                            value={replyInputs[index] || ''}
                            onChange={(e) => handleReplyInputChange(index, e)}
                        />
                        <MDBBtn className="m-0" type="submit" disabled={!replyInputs[index]?.trim()}>
                            <MDBIcon fas icon="paper-plane" />
                        </MDBBtn>
                    </div>
                </form>
            )}
        />
    );

    const renderComment = (commentItem, index, parentCommentId) => {
        return (
            <div key={index} className="mb-2 mt-2">
                <MDBCardBody className={commentItem['is_top_level'] ? "comment-card parent" : "comment-card child"}>
                    <MDBTypography tag="h5" className="text-primary">
                        {commentItem.user.first_name}
                        <span className="small mb-2 text-muted ml-3"> {commentItem[appendLocale('last_updated')]} </span>
                    </MDBTypography>
                    <span>{commentItem.content}</span>
                    <div className="d-flex justify-content-between mt-2 pt-1">
                        <div className="d-flex">
                            <span
                                role="button"
                                className="small d-flex align-items-center text-secondary"
                                onClick={() => handleToggleReply(index)}
                            >
                                <MDBIcon fas icon="reply" />
                                &nbsp;{expand[index] ? dict.hideReplies : dict.showReplies}&nbsp;
                            </span>
                        </div>
                        <div>
                            {commentItem.is_owner && (
                                <div>
                                    <span
                                        role="button"
                                        className="d-flex align-items-center text-danger"
                                        type="delete"
                                        onClick={() => handleDelete(commentItem.id)}
                                    >
                                        <MDBIcon fas icon="trash" className="text-danger" />&nbsp;{dict.delete}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={`p-2 ${expand[index] ? "d-block" : "d-none"}`}>
                        {renderReplyForm(commentItem, index, parentCommentId)}
                        {Array.isArray(commentItem.descendants) && commentItem.descendants.map((reply, replyIndex) => (
                            <div key={replyIndex} className="mb-2">
                                {renderComment(reply, `${index}.${replyIndex}`, reply.id)}
                            </div>
                        ))}
                    </div>
                </MDBCardBody>
            </div>
        );
    };

    return (
        <MDBContainer>
            <MDBRow className="justify-content-center">
                <MDBCardFooter className="bg-light p-3">
                    <Form
                        onSubmit={values => handleSubmit(values)}
                        initialValues={{ comment: '' }}
                        render={({ handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <MDBTextArea
                                    className="bg-white"
                                    label={dict.commentFieldText}
                                    rows={2}
                                    value={input}
                                    onChange={handleInputChange}
                                    name="comment"
                                />
                                <div className="float-end mt-2 pt-1">
                                    <MDBBtn size="sm" type="submit" className="me-1 rounded" disabled={isInputEmpty || isLoading}>
                                        {dict.postComment}
                                    </MDBBtn>
                                </div>
                            </form>
                        )}
                    />
                </MDBCardFooter>
                {Array.isArray(comments) && comments.map((commentItem, index) => (
                    renderComment(commentItem, index, commentItem.id)
                ))}
            </MDBRow>
        </MDBContainer>
    );
};

export default Comment;